import { ArrayMinSize, IsArray, IsNotEmpty, IsNumber, IsString, Length, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';

import getTypeDependsOnWidgetType from 'src/models/helpers/getTypeDependsOnWidgetType';
import { IsPropertyUniqueInStoreSlice } from 'src/actions/validators/IsPropertyUniqueInStoreSlice';

export enum ChatCampaignStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum ChatCampaignTriggerType {
  URL = 'URL',
  TIME_ON_PAGE = 'TIME_ON_PAGE',
  ESTIMATED_WAIT_TIME = 'ESTIMATED_WAIT_TIME',
}

export enum ChatCampaignTriggerMatchType {
  CONTAINS = 'CONTAINS',
  EXACT = 'EXACT',
  REGEX = 'REGEX',
}

export enum ChatCampaignActionType {
  GREETING = 'GREETING',
}

export class ChatCampaign {
  id: string;
  version: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;

  @IsString()
  @IsNotEmpty({ message: 'Campaign name cannot be blank' })
  @IsPropertyUniqueInStoreSlice('chatCampaigns', { message: 'Campaign name already taken! ' })
  name: string;

  @IsString()
  @Length(1, 200, { message: 'You have exceeded the character count' })
  @IsNotEmpty()
  description: string;

  @IsNumber()
  @IsNotEmpty({ message: 'Input a number' })
  priority: number;

  @IsString()
  status: ChatCampaignStatus;

  @IsArray()
  @ValidateNested({ each: true })
  @ArrayMinSize(1, { message: 'Please select at least one trigger' })
  @Type(() => ChatCampaignTrigger)
  triggers: ChatCampaignTrigger[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ChatCampaignAction)
  actions: ChatCampaignAction[];
}

class ChatCampaignAction {
  id: string;
  type: ChatCampaignActionType;

  @ValidateNested()
  @Type(() => ChatCampaignActionGreetingContent)
  content: ChatCampaignActionGreetingContent;
}

class ChatCampaignActionGreetingContent {
  @IsString()
  @IsNotEmpty({ message: 'Greeting cannot be blank' })
  message: string;

  skipOnboarding: boolean;
  ignoreThrottling: boolean;
}

class UrlTriggerContent {
  matchType: ChatCampaignTriggerMatchType;
  @IsString()
  @IsNotEmpty({ message: 'Field cannot be blank' })
  value: string;
}

class TimeOnPageTriggerContent {
  @IsNumber()
  @IsNotEmpty({ message: 'Please enter a number of seconds' })
  limit: number;
}

class EstimatedWaitTimeTriggerContent {
  @IsNumber()
  @IsNotEmpty({ message: 'Please enter a whole number' })
  threshold: number;
}

const TriggerTypeToClass = {
  [ChatCampaignTriggerType.URL]: UrlTriggerContent,
  [ChatCampaignTriggerType.TIME_ON_PAGE]: TimeOnPageTriggerContent,
  [ChatCampaignTriggerType.ESTIMATED_WAIT_TIME]: EstimatedWaitTimeTriggerContent,
};

class ChatCampaignTrigger {
  id: string;

  type: ChatCampaignTriggerType;

  @ValidateNested()
  @Type(getTypeDependsOnWidgetType(['type'], ([type]) => TriggerTypeToClass[type]))
  content: UrlTriggerContent | TimeOnPageTriggerContent | EstimatedWaitTimeTriggerContent;
}
